import { createBrowserRouter } from "react-router-dom";
import MainLayout from "Components/MainLayout/MainLayout";
import ScrollManager from "Components/ScrollManager/ScrollManager";
import NotFound from "Components/404/404";
import ServerError from "Components/500/500";
import { generateHeaderTitles } from "./routing.helpers";
import {
  LayoutDashboard,
  FileText,
  Users,
  Calendar,
  ShieldCheck,
  ShoppingBag,
  BarChart3,
  HelpCircle,
  Settings as SettingsIcon,
  UserCog,
  TicketPercent,
} from "lucide-react";

// Pages
import LandingPage from "pages/public/LandingPage";
import Calculator from "pages/public/Calculator";
import Signup from "pages/Auth/Signup";
import Login from "pages/Auth/Login";
import ApplicationStatus from "pages/Application/ApplicationStatus";
import ApplicationView from "pages/Application/ApplicationView";
import CommunitiesPage from "pages/Communities/Communities";
import CommunitiesList from "pages/Communities/CommunitiesList";
import CommunityDetails from "pages/Communities/Detail/CommunityDetails";
import EventsPage from "pages/Events/Events";
import EventsList from "pages/Events/List/EventsList";
import EventDetail from "pages/Events/EventDetail/EventDetail";
import CreateEvent from "pages/Events/EventDetail/_blocks/CreateEvent";
import CloneEvent from "pages/Events/EventDetail/_blocks/CloneEvent";
import EditEvent from "pages/Events/EventDetail/_blocks/EditEvent";
import DiscountCodesPage from "pages/DiscountCodes/DiscountCodes";
import DiscountCodesList from "pages/DiscountCodes/DiscountCodesList";
import DiscountCodeDetail from "pages/DiscountCodes/DiscountCodeDetail";
import Memberships from "pages/Memberships/Memberships";
import Orders from "pages/Orders/Orders";
import OrdersPage from "pages/Orders/OrdersPage";
import OrderDetail from "pages/Orders/Detail/OrderDetail";
import BookingDetail from "pages/Orders/Detail/BookingDetail";
import Analytics from "pages/Analytics/Analytics";
import HelpSupport from "pages/HelpSupport/HelpSupport";
import Settings from "pages/Settings/Settings";
import TermsOfService from "pages/public/TermsOfService";
import PrivacyPolicy from "pages/public/PrivacyPolicy";
import DashboardHome from "pages/dashboard/DashboardHome";
import SuperAdminLogin from "pages/Auth/SuperAdminLogin";
import Team from "pages/Team/Team";

// Only authenticated routes in sideMenuConfig
export const sideMenuConfig = [
  {
    label: "Dashboard",
    path: "dashboard",
    icon: <LayoutDashboard size={18} />,
    element: <DashboardHome />,
  },
  {
    label: "My Application",
    path: "application",
    element: <ApplicationView />,
    icon: <FileText size={18} />,
  },
  {
    label: "Communities",
    path: "communities",
    element: <CommunitiesPage />,
    rootOnly: true,
    icon: <Users size={18} />,
    children: [
      {
        label: "Communities List",
        path: "",
        element: <CommunitiesList />,
        icon: <Users size={18} />,
      },
      {
        label: "Community Detail",
        path: ":communityId",
        element: <CommunityDetails />,
        hideInMenu: true,
      },
    ],
  },
  {
    label: "Events",
    path: "events",
    element: <EventsPage />,
    icon: <Calendar size={18} />,
    children: [
      {
        label: "Events List",
        path: "",
        element: <EventsList />,
        icon: <Calendar size={18} />,
      },
      {
        label: "Create Event",
        path: "create",
        element: <CreateEvent />,
        hideInMenu: true,
      },
      {
        label: "Clone Event",
        path: "clone/:eventId",
        element: <CloneEvent />,
        hideInMenu: true,
      },
      {
        label: "Event Detail",
        path: ":eventId",
        element: <EventDetail />,
        hideInMenu: true,
      },
      {
        label: "Edit Event",
        path: ":eventId/edit",
        element: <EditEvent />,
        hideInMenu: true,
      },
    ],
  },
  {
    label: "Discount codes",
    path: "discount-codes",
    element: <DiscountCodesPage />,
    // Shown to every organiser while the global codes switch is on (D28, D42).
    requiresCodesEnabled: true,
    icon: <TicketPercent size={18} />,
    children: [
      {
        label: "Discount codes",
        path: "",
        element: <DiscountCodesList />,
        icon: <TicketPercent size={18} />,
      },
      {
        label: "Code Detail",
        path: ":codeId",
        element: <DiscountCodeDetail />,
        hideInMenu: true,
      },
    ],
  },
  {
    label: "Memberships",
    path: "memberships",
    element: <Memberships />,
    rootOnly: true,
    icon: <ShieldCheck size={18} />,
  },
  {
    label: "Orders",
    path: "orders",
    element: <OrdersPage />,
    icon: <ShoppingBag size={18} />,
    children: [
      {
        label: "Orders List",
        path: "",
        element: <Orders />,
        icon: <ShoppingBag size={18} />,
      },
      {
        label: "Order Detail",
        path: "detail/:orderNumber",
        element: <OrderDetail />,
        hideInMenu: true,
      },
      {
        label: "Booking Detail",
        path: "bookings/detail/:bookingNumber",
        element: <BookingDetail />,
        hideInMenu: true,
      },
    ],
  },
  {
    label: "Analytics",
    path: "analytics",
    element: <Analytics />,
    icon: <BarChart3 size={18} />,
  },
  {
    label: "Team",
    path: "team",
    element: <Team />,
    ownerOnly: true,
    icon: <UserCog size={18} />,
  },
  {
    label: "Help & Support",
    path: "help",
    element: <HelpSupport />,
    icon: <HelpCircle size={18} />,
  },
  {
    label: "Settings",
    path: "settings",
    element: <Settings />,
    icon: <SettingsIcon size={18} />,
  },
];

const getRoutingObject = ({ sideMenuConfig }) => {
  const filteredData = sideMenuConfig.filter(
    ({ hideInRouting }) => !hideInRouting,
  );

  const processRoutes = (routes) => {
    return routes.map((route) => {
      const { path, element, children } = route;

      const handle = {
        rootOnly: route.rootOnly,
        ownerOnly: route.ownerOnly,
        requiresCodesEnabled: route.requiresCodesEnabled,
      };

      if (path === "") {
        return { index: true, element, handle };
      }

      if (!path && !children?.length) {
        return null;
      }

      if (!children?.length) {
        return { path, element, handle };
      }

      const processedChildren = processRoutes(children).filter(Boolean);

      return {
        path,
        element,
        handle,
        children: processedChildren,
      };
    });
  };

  return processRoutes(filteredData);
};

export const routing = createBrowserRouter(
  [
    {
      // Pathless layout: resets scroll on navigation and honours #hash links.
      element: <ScrollManager />,
      children: [
        // Public routes (no auth, no sidebar)
        { path: "/", element: <LandingPage /> },
        { path: "/calculator", element: <Calculator /> },
        { path: "/signup", element: <Signup /> },
        { path: "/login", element: <Login /> },
        { path: "/application-status", element: <ApplicationStatus /> },
        { path: "/terms", element: <TermsOfService /> },
        { path: "/privacy", element: <PrivacyPolicy /> },
        { path: "/super-login", element: <SuperAdminLogin /> },

        // Authenticated routes (wrapped in MainLayout with sidebar)
        {
          path: "/shoup-community",
          element: <MainLayout />,
          errorElement: <ServerError />,
          children: getRoutingObject({ sideMenuConfig }),
        },

        // 404
        { path: "*", element: <NotFound /> },
      ],
    },
  ],
  {
    future: {
      v7_startTransition: true,
      v7_relativeSplatPath: true,
    },
  },
);

export const HEADER_TITLES = generateHeaderTitles({ sideMenuConfig });
